/* eslint-disable jsx-a11y/heading-has-content */
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  Box,
  Button,
  CircularProgress,
  Card,
  Grid,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TextField,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';

import {
  floatVal,
  intVal,
  Nullable,
  replaceAt,
  distinct,
} from '@jamesgmarks/utilities';

import {
  StyledTableCell,
  StyledTableRow,
} from '../../parts/mui/StyledTables';

import { Delete as DeleteIcon } from '@mui/icons-material';
import pdfFileIcon from '../../../assets/images/pdf.png';

import { ClientDropdown } from '../../parts/ClientDropdown';
import { Charge } from '../../../../../entities/hydra';
import CreditLine from './CreditLine';
import { CreditModal } from './CreditModal';
import {
  EHydraCreditNoteState,
  getHydraCreditNoteStateDisplayName,
} from '../../../entity-interfaces/EHydraCreditNoteState';
import { EHydraState } from '../../../entity-interfaces/IInvoice';
import { IClientDropdownItem } from '../../../entity-interfaces/IClientDropdownItem';
import { ICredit } from '../../../interfaces/ICredit';
import { ICreditNote } from '../../../interfaces/ICreditNote';
import { IOwnershipGroupDropdownItem } from '../../../entity-interfaces/IOwnershipGroupDropdownItem';
import { OwnershipGroupDropdown } from '../../parts/OwnershipGroupDropdown';
import { toDollarAmount } from '../../../app-utils';
import {
  getCreditNoteAvailableCredit,
  getCreditNoteTotal,
  sumActiveCreditsApplied,
} from '../../../app-utils/credit-notes';
import { strVal } from '@jamesgmarks/utilities';
import { distinctOn } from '@jamesgmarks/utilities';
import { capitalize, getPartsFromIsoString } from '../../../app-utils/helpers';
import { ConfirmationModal } from '../../parts/ConfirmationModal';
import { CreditNoteNotesSection } from './CreditNoteNotesSection';
import { CreditNoteVoidSection } from './CreditNoteVoidSection';
import { getCreditInvoiceLabel } from './creditInvoiceLabel';
import {
  applyCreditBalanceToSourceInvoices,
  publishCreditNotes,
  triggerCreditNotePdfGeneration,
} from '../../../redux/features/credit-notes/actions';
import { REACT_APP_API_ROOT_URI } from '../../../App';
import { EUploadStatus } from '../../../entity-interfaces/IPdfGeneration';
import { displayFile } from '../../../app-utils/display-file';
import { getCreditNoteDate } from './CreditView';
import { typedApiFetch } from '../../../redux/utils';
import { showMessage } from '../../../redux/features/messaging/actions';
import { Link, useHistory } from 'react-router-dom';
import { ISubscriptions } from '@llws/lift-entity-interfaces';
import { formatDecimalToPercentage, roundFloatToCents, roundFloatToCentsAsDollar } from '@hydra/internal';
import { ApplyToInvoiceModal } from './ApplyToInvoiceModal';
import { TCreditNoteWithBillingContacts } from '../../../types/TCreditNoteWithBillingContacts';

const createEmptyCreditNoteObject = () => (
  {
    billingAddress: null,
    credits: [] as ICredit[],
    currencyCode: 'CAD',
    thirdPartyCreditNoteId: null,
    thirdParty: null,
    noteDate: getCreditNoteDate(),
    notes: null,
    ownershipGroupId: null,
    pdfGenerations: [],
    state: EHydraCreditNoteState.unpublished,
    subtotalAmount: '0',
    taxAmount: '0.00',
    taxName: 'No Tax',
    taxRate: '0',
    voidState: null,
    voidRequestReason: null,
    voidApproveOrDenyReason: null,
  } as ICreditNote
);

const convertIsoStringToDate = (isoString: string) => {
  const [year, month, dateOfMonth] = isoString.slice(0, 10).split('-');
  return new Date(intVal(year), intVal(month) - 1, intVal(dateOfMonth));
};

/**
 * Props for {@link CreditNoteSingleView}.
 */
interface ICreditNoteSingleViewProps {
  creditNote?: Nullable<ICreditNote>,
  onSave: (note: ICreditNote) => Promise<Nullable<ICreditNote>> | void,
}

export const CreditNoteSingleView = ({
  creditNote: incomingCreditNote,
  onSave,
}: ICreditNoteSingleViewProps) => {
  const history = useHistory();

  const [creditNote, setCreditNote] = useState(incomingCreditNote ?? createEmptyCreditNoteObject());
  const [noteDate, setNoteDate] = useState(convertIsoStringToDate(creditNote.noteDate));
  const [searchClientId, setSearchClientId] = useState<Nullable<number>>(creditNote?.clientId ?? null);
  const [searchClient, setSearchClient] = useState<Nullable<IClientDropdownItem>>(null);
  const [searchOwnershipGroupId, setSearchOwnershipGroupId] = (
    useState<Nullable<number>>(creditNote?.ownershipGroupId ?? null)
  );
  const [searchOwnershipGroup, setSearchOwnershipGroup] = useState<Nullable<IOwnershipGroupDropdownItem>>(null);
  const [allowEdits, setAllowEdits] = useState(creditNote?.id ? false : true);

  const [showSubscriptionSelectModal, setShowSubscriptionSelectModal] = useState(false);

  const [clawbackFromPartner] = useState(true);

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [confirmationModalOnConfirm, setConfirmationModalOnConfirm] = useState<() => void>(() => { });
  const [confirmationModalPromptText, setConfirmationModalPromptText] = useState('');
  const [showApplyToInvoiceModal, setShowApplyToInvoiceModal] = useState(false);

  const [showApplyAllModal, setShowApplyAllModal] = useState(false);

  const [creditLineAmountOverride, setCreditLineAmountOverride] = useState<Nullable<number>>(null);
  const [creditLineDescriptionOverride, setCreditLineDescriptionOverride] = useState<Nullable<string>>(null);

  /** Tracks the ID of the credit application currently being unapplied. Non-null while a DELETE request is in flight. */
  const [pendingUnapplyId, setPendingUnapplyId] = useState<Nullable<number>>(null);
  /** Tracks whether a bulk unapply request is in flight. */
  const [isUnapplyingAll, setIsUnapplyingAll] = useState(false);

  /** Loading state for Create and Create & Publish operations */
  const [isCreatingCreditNote, setIsCreatingCreditNote] = useState(false);
  const [isPublishingCreditNote, setIsPublishingCreditNote] = useState(false);

  useEffect(() => {
    setCreditNote((oldCreditNote) => updatedCreditNoteInfo({
      ...oldCreditNote,
      credits: (
        (oldCreditNote.credits ?? []).map((credit) => ({
          ...credit,
          clawbackFromPartner,
        }))
      ),
    }));
  }, [clawbackFromPartner]);

  useEffect(() => {
    !allowEdits
      ? setSearchClientId(creditNote.clientId ?? null)
      : (
        setCreditNote((oldCreditNote) => ({
          ...oldCreditNote,
          billingAddress: searchClient?.billingAddress ?? {},
          billingAccountId: searchClient?.billingAccountId,
        }))
      );
  }, [creditNote.clientId, allowEdits, searchClient]);

  useEffect(() => {
    !allowEdits
      ? setSearchOwnershipGroupId(creditNote.ownershipGroupId ?? null)
      : (
        setCreditNote((oldCreditNote) => ({
          ...oldCreditNote,
          billingAddress: searchOwnershipGroup?.billingAddress ?? {},
          ownershipGroupId: searchOwnershipGroup?.id,
        }))
      );
  }, [creditNote.ownershipGroupId, allowEdits, searchOwnershipGroup]);

  useEffect(() => {
    if (incomingCreditNote) {
      setCreditNote(incomingCreditNote);
    }
  }, [incomingCreditNote]);

  // TODO: AMIR AMIR AMIR AMIR AMIR AMIR AMIR
  // When credit lines change, update creditNote via setCreditNote
  // Same with client / ownership group, etc.

  const updatedCreditNoteInfo = (newCreditNote: ICreditNote): ICreditNote => {
    const firstCredit = newCreditNote.credits[0] ?? null;

    const creditNoteSubtotalAmount = (
      (newCreditNote.credits ?? []).reduce((acc, credit) => acc + floatVal(credit.amount), 0)
    );
    const creditNoteTaxAmount = (
      (newCreditNote.credits ?? []).reduce((acc, credit) => acc + floatVal(credit.taxAmount), 0)
    );

    const returnCreditNote: ICreditNote = {
      ...newCreditNote,
      currencyCode: firstCredit?.currencyCode ?? '',
      noteDate: newCreditNote.noteDate ?? getCreditNoteDate(),
      taxAmount: roundFloatToCentsAsDollar(creditNoteTaxAmount).replace('$', ''),
      subtotalAmount: `${creditNoteSubtotalAmount}`,
    };

    return returnCreditNote;
  };

  const creditLineChanged = (credit: ICredit, index: number) => {
    setCreditNote((oldCreditNote) => updatedCreditNoteInfo({
      ...oldCreditNote,
      credits: replaceAt(oldCreditNote.credits ?? [], index, credit),
    }));
  };

  const removeCreditLine = (index: number) => {
    setCreditNote((oldCreditNote) => {
      const updatedCredits = (oldCreditNote.credits ?? []).filter((_, i) => i !== index);

      const updated = updatedCreditNoteInfo(
        {
          ...oldCreditNote,
          credits: updatedCredits,
        },
      );

      return updated;
    });
  };

  const addCreditLine = useCallback((newCredit: ICredit) => {
    setCreditNote((oldCreditNote) => updatedCreditNoteInfo({
      ...oldCreditNote,
      credits: [
        ...(oldCreditNote.credits ?? []),
        newCredit,
      ],
    }));
  }, []);

  const createUnlinkedCredit = async () => {
    if (!creditNote.billingAccountId) {
      alert(
        'A billing account does not exist for this credit note. '
        + 'We have bigger problems than not adding a credit line.',
      );
      return;
    }

    addCreditLine({
      freshbooksClientId: creditNote.billingAccountId,
      creditNumber: null,
      clientId: creditNote.clientId ?? null,
      partnerId: null,
      partnerClawback: null,
      clawbackFromPartner: false,
      invoiceId: null,
      subscriptionId: null,
      generatedSubscriptionId: null,
      currencyCode: 'CAD', // TODO: Determine currency by credit note parameters? (ie. client, og) make it so this is editable in the UI?
      amount: '5.00', // Default value, editable in the UI
      taxRate: '0', // TODO make it so this is editable in the UI
      taxAmount: '0', // TODO make it so this is editable in the UI
      description: 'Unlinked Credit',
      hydraState: 'active',
      creditDate: new Date(creditNote.noteDate),
      invoiceNumber: null,
      taxName: 'EXEMPT', // TODO make it so this is editable in the UI
    });
  };

  const createSubscriptionCredit = useCallback(
    async () => {
      if (allowEdits && searchClient?.id) {
        setShowSubscriptionSelectModal(true);
      }
    },
    [allowEdits, searchClient?.id],
  );

  const addCreditsForCharge = useCallback(
    (charge: Charge, subscription: ISubscriptions, invoiceIds: string[]) => {
      if (!creditNote.billingAccountId) {
        alert(
          'A billing account does not exist for this credit note. '
          + 'We have bigger problems than not adding a credit line.',
        );
        return;
      }
      if (!subscription.id) {
        alert('No subscription ID');
        return;
      }
      if (!subscription.baseSubscription) {
        alert('No base subscription');
        return;
      }

      // TODO: Validate that the charge can be placed into a single credit... if spread over multiple tax rates or w/e, we may have a problem.s
      return distinctOn(charge.chargeHasTaxes, (a, b) => a.invoiceId === b.invoiceId)
        .map((cht) => {
          if (!invoiceIds.includes(strVal(cht.invoiceId))) {
            return null;
          }

          const creditAmount = cht.invoice.lines.reduce(
            (acc, cur) =>
              (cur.lineType !== 'charge' || cur.chargeId !== charge.id)
                ? acc
                : acc + floatVal(cur.lineTotal)
            , 0,
          );

          // Both money values are rounded to whole cents before they leave this callback.
          // `creditAmount` is a float reduction over invoice lines and the tax is a float
          // product of a rate and that sum, so unrounded they reach `credits.amount` /
          // `credits.tax_amount` verbatim (`saveCreditWithManager` persists the string as
          // given) and make the note's `subtotal_amount`/`tax_amount` — which are the
          // cent-rounded sums of these columns — no longer equal the exact sum of its lines.
          //
          // `roundFloatToCents` is the right rounder here despite the negative tax: it rounds
          // the magnitude (`Math.abs`) and reapplies the sign, so it is symmetric
          // half-away-from-zero (-1.105 -> -1.11, mirroring 1.105 -> 1.11) rather than the
          // +Infinity-biased -1.10 a naive `Math.round` on the signed value would give. Its
          // `+ 1e-10` nudge is applied to that magnitude and repairs float representation
          // error rather than adding bias: 0.05 * 42.3 evaluates to 2.1149999999999998, whose
          // true decimal value is 2.115, and only the nudge rounds it up to 2.12.
          //
          // The negative sign on the tax is this button's pre-existing convention and is left
          // as it is; only the precision changes here.
          const creditTaxAmount = roundFloatToCents(-1 * floatVal(cht.taxRate ?? '0.00') * creditAmount);
          const creditLineToAdd = {
            freshbooksClientId: creditNote.billingAccountId,
            creditNumber: null,
            clientId: creditNote.clientId ?? null,
            partnerId: subscription.baseSubscription?.partnerId ?? null,
            clawbackFromPartner,
            invoiceId: intVal(cht.invoice.id) ?? null, // TODO: rename property name to `number`?
            invoiceNumber: cht.invoice.invoiceNumber ?? null,
            invoiceDate: getPartsFromIsoString(`${cht.invoice.invoiceDate}`, 'date'),
            subscriptionId: subscription.id,
            generatedSubscription: charge,
            generatedSubscriptionId: charge.id,
            currencyCode: cht.currency ?? 'CAD',
            amount: roundFloatToCents(creditAmount).toFixed(2),
            taxRate: cht.taxRate ?? '0.00',
            taxAmount: creditTaxAmount.toFixed(2),
            taxName: cht.taxName,
            description: `Credit for ${subscription.invoiceDescription}`, // TODO: Get invoice number - note manager would be awful for this right now.
            hydraState: 'active',
            creditDate: new Date(cht.invoice.invoiceDate),
          } as ICredit;

          addCreditLine(creditLineToAdd);

          return creditLineToAdd;
        });
    }, [
      addCreditLine,
      clawbackFromPartner,
      creditNote.billingAccountId,
      creditNote.clientId,
    ],
  );

  const subtotal = useMemo(() => floatVal(creditNote?.subtotalAmount ?? 0), [creditNote]);

  const taxes = useMemo(
    () => creditNote.credits.reduce((total, credit) => total + floatVal(credit.taxAmount ?? 0), 0),
    [creditNote],
  );

  const creditApplications = useMemo(
    () => (creditNote.creditsApplied ?? []).filter(application => application.hydraState === 'active'),
    [creditNote],
  );

  // NOTE: `taxes` above is derived from the credit LINES rather than read from `creditNote.taxAmount`,
  // because this view doubles as the editor and must total an unsaved note. The amounts are fed into
  // the same shared helpers the list view uses, so the two screens cannot disagree on the formula.
  const totalCreditAmount = useMemo(
    () => getCreditNoteTotal({ subtotalAmount: subtotal, taxAmount: taxes }),
    [subtotal, taxes],
  );

  const totalAppliedAmount = useMemo(
    () => sumActiveCreditsApplied(creditApplications),
    [creditApplications],
  );

  const availableCreditAmount = useMemo(
    () => getCreditNoteAvailableCredit(
      { subtotalAmount: subtotal, taxAmount: taxes },
      totalAppliedAmount,
    ),
    [subtotal, taxes, totalAppliedAmount],
  );

  const theme: Theme = useTheme();

  const handleChargeSelected = useCallback((charges: Charge[], subscription: ISubscriptions, invoiceIds: number[]) => {
    // For each charge, process only the selected invoice IDs
    charges.forEach((charge: Charge) => {
      // Filter to only the invoice IDs that belong to this specific charge and were selected
      const chargeSpecificInvoiceIds = distinct(
        (charge.chargeHasTaxes ?? [])
          .filter((cht) => (
            ![EHydraState.deleted, EHydraState.unsent].includes(cht.invoice.hydraState)
            && invoiceIds.includes(cht.invoiceId)
          ))
          .map((cht) => strVal(cht.invoiceId)),
      );
      addCreditsForCharge(charge, subscription, chargeSpecificInvoiceIds);
    });
    setShowSubscriptionSelectModal(false);
  }, [addCreditsForCharge]);

  const canBeDeleted = (
    [EHydraCreditNoteState.unpublished, EHydraCreditNoteState.draft].includes(creditNote.state) && creditNote.id
  );

  /** `true` when the credit note exists and is in a state that can be published (not already sent, deleted, or void). */
  const canPublish = (
    !!creditNote.id
    && ![
      EHydraCreditNoteState.sent,
      EHydraCreditNoteState.deleted,
      EHydraCreditNoteState.void,
    ].includes(creditNote.state)
  );

  const hasDownload = (
    (creditNote.pdfGenerations ?? []).filter((g) => g.uploadStatus === EUploadStatus.completed).length > 0
  );

  const viewGcpCreditNotePdf = async () => {
    if (!creditNote.noteNumber) {
      console.info('This credit note has not been created yet.');
      return;
    }

    const url = (
      `${REACT_APP_API_ROOT_URI}/downloads/download_credit_note_pdf/${creditNote.noteNumber}`
    );

    const filename = `CN_${creditNote.noteNumber}`;

    await displayFile(url, filename);
  };

  /**
   * Sends a `DELETE /credit_applied/:id` request to remove a credit application.
   * Sets `pendingUnapplyId` to the target ID before the request, which disables all
   * Unapply buttons and shows a spinner on the active button while the request is in flight.
   * `pendingUnapplyId` is cleared in a `finally` block on both success and error paths.
   * On success, displays a confirmation message and reloads the page so the updated
   * credit note state is reflected in the UI.
   *
   * @param applicationId - The ID of the `CreditApplied` record to delete.
   *   If `null` or `undefined`, an error message is shown and the request is aborted.
   */
  const handleUnapplyCreditApplication = useCallback(
    async (applicationId: Nullable<number>) => {
      if (!applicationId) {
        showMessage({
          message: 'Cannot unapply: invalid application ID.',
          severity: 'error',
        });
        return;
      }

      if (isUnapplyingAll) {
        return;
      }

      setPendingUnapplyId(applicationId);

      try {
        const url = `${REACT_APP_API_ROOT_URI}/credit_applied/${applicationId}`;
        const response = await typedApiFetch(url, {
          method: 'DELETE',
        });

        if (!response.ok) {
          throw new Error('Failed to unapply credit application');
        }

        showMessage({
          message: 'Credit application unapplied successfully.',
          severity: 'success',
        });

        // Refresh the credit note data
        if (creditNote.id) {
          // Re-fetch the credit note from parent or reload
          window.location.reload();
        }
      } catch (error) {
        console.error('Error unapplying credit application:', error);
        showMessage({
          message: `Failed to unapply credit application: ${error instanceof Error ? error.message : 'Unknown error'}`,
          severity: 'error',
        });
      } finally {
        setPendingUnapplyId(null);
      }
    },
    [creditNote.id, isUnapplyingAll],
  );

  /**
   * Bulk-unapplies all active credit applications on the current credit note.
   *
   * Collects the IDs of every application currently in the `creditApplications` prop,
   * sends a `DELETE /credit_applied/bulk` request with those IDs, and reloads the page
   * on success so the updated credit note state is reflected in the UI.
   *
   * While the request is in flight, `isUnapplyingAll` is set to `true`, which disables
   * the "Unapply All" button, all individual "Unapply" buttons, and prevents
   * `handleUnapplyCreditApplication` from firing so that concurrent unapply operations
   * cannot occur.
   */
  const handleUnapplyAllCreditApplications = useCallback(async () => {
    const activeApplicationIds = creditApplications
      .map((application) => application.id ?? null)
      .filter((applicationId): applicationId is number => !!applicationId);

    if (activeApplicationIds.length === 0) {
      showMessage({
        message: 'No active credit applications available to unapply.',
        severity: 'warning',
      });
      return;
    }

    setIsUnapplyingAll(true);

    try {
      const response = await typedApiFetch(`${REACT_APP_API_ROOT_URI}/credit_applied/bulk`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          creditApplicationIds: activeApplicationIds,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to unapply all credit applications.');
      }

      showMessage({
        message: 'All credit applications unapplied successfully.',
        severity: 'success',
      });

      if (creditNote.id) {
        window.location.reload();
      }
    } catch (error) {
      console.error('Error bulk unapplying credit applications:', error);
      const errorMessage = (
        error instanceof Error
          ? error.message
          : 'Unknown error'
      );
      showMessage({
        message: `Failed to unapply all credit applications: ${errorMessage}`,
        severity: 'error',
      });
    } finally {
      setIsUnapplyingAll(false);
    }
  }, [creditApplications, creditNote.id]);

  /**
   * Handles the "Create" and "Create & Publish" button actions for new credit notes.
   *
   * Calls `onSave` to persist the credit note and, if a new ID was returned (i.e. this
   * was a create rather than an update), optionally publishes it immediately when
   * `publishAfterSave` is `true`. After either path the user is redirected to the
   * newly created credit note's detail page.
   *
   * When the credit note already has an ID (edit mode), the function simply disables
   * editing after saving.
   *
   * @param publishAfterSave - When `true`, the credit note is published immediately after
   *   being created. Corresponds to the "Create & Publish" button.
   */
  const handleCreateCreditNote = useCallback(async (publishAfterSave: boolean) => {
    setIsCreatingCreditNote(true);
    try {
      const savedCreditNote = await onSave(creditNote);

      if (!creditNote.id && savedCreditNote?.id) {
        if (publishAfterSave) {
          await publishCreditNotes([savedCreditNote.id], { shouldBroadcast: false });
        }

        history.push(`/credit_notes/${savedCreditNote.id}`);
        return;
      }

      setAllowEdits(false);
    } catch (error) {
      console.error('Failed to save credit note:', error);
      showMessage({
        message: `Failed to save credit note: ${error instanceof Error ? error.message : 'Unknown error'}`,
        severity: 'error',
      });
    } finally {
      setIsCreatingCreditNote(false);
    }
  }, [creditNote, history, onSave]);

  /**
   * Publishes the current credit note directly from the single-view page.
   *
   * Calls `publishCreditNotes` with `shouldBroadcast: false` to avoid triggering
   * a real-time broadcast event. On success, updates the local credit note state
   * to `sent` so the UI reflects the new state immediately without a full data refresh.
   * On failure, surfaces a user-facing error message via `showMessage` and logs the
   * underlying error to the console. The publish-in-progress flag is always cleared
   * in the `finally` block regardless of outcome.
   */
  const handlePublishCreditNote = useCallback(async () => {
    if (!creditNote.id) {
      return;
    }

    setIsPublishingCreditNote(true);
    try {
      const didPublish = await publishCreditNotes([creditNote.id], { shouldBroadcast: false });

      if (didPublish) {
        setCreditNote((oldCreditNote) => ({
          ...oldCreditNote,
          state: EHydraCreditNoteState.sent,
        }));
      }
    } catch (err) {
      console.error('Failed to publish credit note:', err);
      showMessage({
        message: `Failed to publish credit note: ${err instanceof Error ? err.message : 'Unknown error'}`,
        severity: 'error',
      });
    } finally {
      setIsPublishingCreditNote(false);
    }
  }, [creditNote.id]);

  return (
    <>
      <Grid item mx='auto' px={15} mt={2} mb={5} xs={8}>
        <Link to='/credit_notes'>
          <Button variant='contained' color='info'>&larr; View All Credit Notes</Button>
        </Link>
      </Grid>

      <Grid sx={{ mx: 'auto', px: 15, paddingBottom: '2rem' }}>
        <Typography
          sx={{ color: '#18a558' }}
          variant='h5'
        >
          <u>{allowEdits ? 'CREATE MODE' : 'VIEW MODE'}</u>
          {
            hasDownload
            && (
              <a
                href='#'
                style={{ marginLeft: 15 }}
                onClick={() => viewGcpCreditNotePdf()}
              >
                <img
                  src={pdfFileIcon}
                  alt="pdf"
                  height="20px"
                  style={{ filter: `grayscale(${hasDownload ? '0' : '1'})` }}
                  title='Download available'
                />
              </a>
            )
          }

          <Button
            color='secondary'
            variant='contained'
            size='small'
            sx={{ fontSize: '0.9rem', marginLeft: 4 }}
            onClick={() => creditNote.id && triggerCreditNotePdfGeneration([creditNote.id])}
          >
            Generate PDF
          </Button>

          <Button
            color='primary'
            variant='contained'
            size='small'
            sx={{ fontSize: '0.9rem', marginLeft: 2 }}
            disabled={!hasDownload}
            onClick={() => viewGcpCreditNotePdf()}
          >
            Download PDF
          </Button>

        </Typography>

        <br />

        <Grid container spacing={4}>
          <Grid item xs={12} md={5}>
            <TableContainer component={Paper}>
              <Table>
                <TableBody>
                  <StyledTableRow dark={true}>
                    <StyledTableCell dark={true} align='right'><b>Credit Note #</b>:</StyledTableCell>
                    <StyledTableCell dark={true} align='left'>
                      {`${creditNote.noteNumber ?? ''}`}
                    </StyledTableCell>
                  </StyledTableRow>

                  <StyledTableRow dark={true}>
                    <StyledTableCell dark={true} align='right'><b>State</b>:</StyledTableCell>
                    <StyledTableCell dark={true} align='left'>
                      {getHydraCreditNoteStateDisplayName(creditNote.state)}
                      {creditNote.voidState && ` (${capitalize(creditNote.voidState.replace(/[_-]/g, ' '))})`}
                    </StyledTableCell>
                  </StyledTableRow>

                  <StyledTableRow dark={true}>
                    <StyledTableCell dark={true} align='right'><b>Note Date</b>:</StyledTableCell>
                    <StyledTableCell dark={true} align='left'>
                      {
                        allowEdits
                          ?
                          <TextField
                            size='small'
                            type='date'
                            sx={{ input: { color: 'white' } }}
                            value={noteDate.toLocaleDateString()}
                            onChange={
                              ({ target: { value } }) => {
                                setNoteDate((old) => {
                                  const newNoteDate = (
                                    value
                                      ? convertIsoStringToDate(value)
                                      : new Date((new Date()).setHours(0, 0, 0, 0))
                                  );

                                  setCreditNote((old) => ({ ...old, noteDate: newNoteDate.toISOString() }));

                                  return newNoteDate;
                                });
                              }
                            }
                          />
                          :
                          creditNote.noteDate.slice(0, 10)
                      }
                    </StyledTableCell>
                  </StyledTableRow>
                </TableBody>
              </Table>
            </TableContainer>

            {searchClientId && (
              <CreditModal
                searchClientId={searchClientId}
                show={showSubscriptionSelectModal}
                onChargeSelected={handleChargeSelected}
                onHide={() => setShowSubscriptionSelectModal(false)}
              />
            )}
          </Grid>

          <Grid item xs={12} md={7}>
            <Card style={{ padding: '.75rem' }}>
              {
                allowEdits
                && (
                  <Button
                    color='warning'
                    onClick={() => {
                      setSearchClient(null);
                      setSearchClientId(null);
                      setSearchOwnershipGroup(null);
                      setSearchOwnershipGroupId(null);
                      setCreditNote(incomingCreditNote ?? createEmptyCreditNoteObject());
                    }}
                    variant='text'
                  >
                    Clear
                  </Button>
                )
              }
              {
                searchClient
                  ? (
                    <Box mt={1}>
                      <Typography sx={{ fontSize: '0.8rem', color: 'gray' }}><u>Client</u></Typography>
                      <Typography variant='h6'>{searchClient.name}</Typography>
                    </Box>
                  )
                  : (
                    <Box sx={{ mt: 0.5 }}>
                      <ClientDropdown
                        allowFreeform={false}
                        clientId={searchClientId}
                        onClientChanged={(client) => {
                          setSearchClientId(client ? intVal(client.id) : null);
                          setSearchClient(client);
                        }}
                        onClientFreeForm={(freeFormName: Nullable<string>) => { }}
                      />
                    </Box>
                  )
              }
              {
                searchOwnershipGroup
                  ? (
                    <>
                      <Typography sx={{ fontSize: '0.8rem', color: 'gray' }}><u>Ownership Group</u></Typography>
                      <Typography variant='h6'>{searchOwnershipGroup.name}</Typography>
                    </>
                  )
                  : (
                    allowEdits
                      ? (
                        <Box sx={{ mt: 1.5 }}>
                          <OwnershipGroupDropdown
                            disableIfNoOwnershipGroups
                            freshbooksClientId={searchClient?.freshbooksClientId}
                            allowFreeform={false}
                            ownershipGroupId={searchOwnershipGroupId}
                            onOwnershipGroupChanged={(ownershipGroup) => {
                              setSearchOwnershipGroupId(ownershipGroup ? intVal(ownershipGroup.id) : null);
                              setSearchOwnershipGroup(ownershipGroup ?? null);
                            }}
                            onOwnershipGroupFreeForm={(ownershipGroupString) => {
                              // TODO: Remove free-form support for ths one.
                            }}
                          />
                        </Box>
                      )
                      : null
                  )
              }
            </Card>

            {
              searchClient && creditNote?.billingAddress?.street1
                ? (
                  <Card sx={{ mt: 1, pl: 1.5, pt: 1.5, pb: 1 }}>
                    {
                      creditNote.billingAddress?.organization
                      && <Typography variant='subtitle1'>{creditNote.billingAddress?.organization}</Typography>
                    }
                    {
                      creditNote.billingAddress?.street1
                      && <Typography variant='subtitle1'>{creditNote.billingAddress?.street1}</Typography>
                    }
                    {
                      creditNote.billingAddress?.street2
                      && <Typography variant='subtitle1'>{creditNote.billingAddress?.street2}</Typography>
                    }
                    {
                      creditNote.billingAddress?.city
                      && <Typography component='span'>{`${creditNote.billingAddress?.city}`}</Typography>
                    }
                    {
                      creditNote.billingAddress?.province
                      && <Typography component='span'>{`, ${creditNote.billingAddress?.province}`}</Typography>
                    }
                    {
                      creditNote.billingAddress?.postalCode
                      && <Typography component='span'>{`, ${creditNote.billingAddress?.postalCode}`}</Typography>
                    }

                    {
                      creditNote.billingAddress?.country
                      && <Typography variant='subtitle1'>{creditNote.billingAddress?.country}</Typography>}
                  </Card>
                )
                : (
                  searchClient
                  && (
                    <Card sx={{ mt: 1, pl: 1.5, pt: 1.5, pb: 1 }}>
                      <Typography>Credit note has no billing address.</Typography>
                    </Card>
                  )
                )
            }
          </Grid>
        </Grid>

        <br />

        {/* Apply All to Source Invoices temporarily hidden */}

        {
          !allowEdits
          && (
            <Box mb={2} display='flex' gap={1}>
              {
                canPublish
                && (
                  <Button
                    variant='contained'
                    color='secondary'
                    onClick={handlePublishCreditNote}
                    disabled={isPublishingCreditNote}
                    startIcon={isPublishingCreditNote ? <CircularProgress size={20} color='inherit' /> : undefined}
                  >
                    {isPublishingCreditNote ? 'Publishing...' : 'Publish Credit Note'}
                  </Button>
                )
              }
              <Button
                variant='contained'
                color='primary'
                onClick={() => setShowApplyToInvoiceModal(true)}
                disabled={creditNote.state !== EHydraCreditNoteState.sent || isPublishingCreditNote}
                title={creditNote.state !== EHydraCreditNoteState.sent
                  ? 'Credit note must be published before applying to invoice'
                  : ''}
              >
                Apply Credit Note
              </Button>
            </Box>
          )
        }

        {
          allowEdits && (
            <Box mb={2} display="flex" gap={2}>
              <Button
                variant="contained"
                color="primary"
                onClick={createUnlinkedCredit}
              >
                Add Unlinked Credit
              </Button>
              <Button
                variant="contained"
                color="secondary"
                onClick={createSubscriptionCredit}
                disabled={!searchClient}
              >
                Add Subscription Credit
              </Button>
            </Box>
          )
        }

        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <StyledTableRow>
                <StyledTableCell>Item</StyledTableCell>
                <StyledTableCell>Description</StyledTableCell>
                <StyledTableCell>Unit Cost</StyledTableCell>
                <StyledTableCell>&nbsp;</StyledTableCell>
              </StyledTableRow>
            </TableHead>
            <TableBody>
              {
                (creditNote.credits ?? []).length === 0
                && (
                  <StyledTableRow sx={{ backgroundColor: 'white' }}>
                    <StyledTableCell colSpan={4} sx={{ textAlign: 'center', py: 3, color: '#999' }}>
                      No credits added yet. Use the buttons above to add subscription or unlinked credits.
                    </StyledTableCell>
                  </StyledTableRow>
                )
              }
              {
                creditNote.credits?.map((credit, index) => (
                  <CreditLine
                    key={index}
                    credit={credit}
                    allowEdits={allowEdits}
                    onChange={(changedCredit) => creditLineChanged(changedCredit, index)}
                    removeFn={() => {
                      removeCreditLine(index);
                    }}
                    amountOverride={creditLineAmountOverride}
                    setAmountOverride={setCreditLineAmountOverride}
                    descriptionOverride={creditLineDescriptionOverride}
                    setDescriptionOverride={setCreditLineDescriptionOverride}
                  />
                ))
              }
            </TableBody>
          </Table>
        </TableContainer>
        <Paper>
          <Grid container my={2} sx={{ border: '1px solid #d3d3d3' }}>
            <Grid item container xs={12} px={2} pt={1} sx={{ backgroundColor: theme.palette.action.hover }}>
              <Grid item xs={8}>Subtotal</Grid>
              <Grid item xs={4} pr={1} textAlign='right'>{toDollarAmount(floatVal(creditNote.subtotalAmount))}</Grid>
            </Grid>
            <Grid item container xs={12} px={2} sx={{ backgroundColor: theme.palette.action.hover }}>
              <Grid item xs={8}>Tax Total</Grid>
              <Grid item xs={4} pr={1} textAlign='right'>{toDollarAmount(taxes)}</Grid>
            </Grid>
            <Grid item container xs={12} pl={4} pr={2} pb={1} sx={{ backgroundColor: theme.palette.action.hover }}>
              {
                creditNote.credits.map((credit) => {
                  return <>
                    <Grid item xs={8}>
                      <i>
                        {
                          `${getCreditInvoiceLabel(credit).text} - `
                          + `${credit.taxName ?? creditNote.taxName}`
                          + ` `
                          + `(${formatDecimalToPercentage(credit.taxRate ?? creditNote.taxRate)}%)`
                          + `${credit.subscriptionId ? ` - Sub: ${credit.subscriptionId}` : ''}`
                        }
                      </i>
                    </Grid>
                    <Grid item xs={4} pr={1} textAlign='right'>
                      <i>{toDollarAmount(floatVal(credit.taxAmount ?? creditNote.taxAmount))}</i>
                    </Grid>
                  </>;
                })
              }

            </Grid>

            <Grid
              container
              item
              mt={0}
              px={2}
              py={0.5}
              sx={{ color: 'white', backgroundColor: 'rgba(30,144,255,0.8)', border: '1px solid #7e7e7e' }}
              xs={12}
            >
              <Grid item xs={8}><b>TOTAL</b></Grid>
              <Grid item xs={4} pr={1} textAlign='right'><b>{toDollarAmount(subtotal + taxes)}</b></Grid>
            </Grid>
          </Grid>
        </Paper>

        {
          creditApplications.length > 0
          && (
            <Paper sx={{ mt: 2, p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant='h6'>Credit Applications</Typography>
                <Button
                  size='small'
                  color='error'
                  variant='outlined'
                  startIcon={isUnapplyingAll ? <CircularProgress size={14} color='inherit' /> : <DeleteIcon />}
                  onClick={handleUnapplyAllCreditApplications}
                  disabled={isUnapplyingAll || pendingUnapplyId !== null || creditApplications.length === 0}
                  title='Unapply all active credit applications on this credit note'
                >
                  {isUnapplyingAll ? 'Unapplying All…' : 'Unapply All'}
                </Button>
              </Box>

              <Grid container spacing={1} sx={{ mt: 0.5, mb: 1.5 }}>
                <Grid item xs={12} md={4}>
                  <Typography variant='body2'>Total Credit: <b>{toDollarAmount(totalCreditAmount)}</b></Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant='body2'>Total Applied: <b>{toDollarAmount(totalAppliedAmount)} </b></Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant='body2'>
                    Available Credit: <b>{toDollarAmount(availableCreditAmount)}</b>
                  </Typography>
                </Grid>
              </Grid>

              <TableContainer component={Paper}>
                <Table size='small'>
                  <TableHead>
                    <StyledTableRow>
                      <StyledTableCell>Date Applied</StyledTableCell>
                      <StyledTableCell>Invoice #</StyledTableCell>
                      <StyledTableCell >Amount</StyledTableCell>
                      <StyledTableCell >Tax</StyledTableCell>
                      <StyledTableCell >Total</StyledTableCell>
                      <StyledTableCell>State</StyledTableCell>
                      <StyledTableCell>Actions</StyledTableCell>
                    </StyledTableRow>
                  </TableHead>

                  <TableBody>
                    {
                      creditApplications.map((application, index) => {
                        const applicationTotal = (
                          floatVal(application.amount ?? 0) 
                        + floatVal(application.taxAmount ?? 0)
                        );
                        const dateApplied = application.dateApplied ? new Date(application.dateApplied) : null;
                        const dateAppliedText = (
                          dateApplied && !Number.isNaN(dateApplied.getTime())
                            ? dateApplied.toLocaleDateString()
                            : 'N/A'
                        );

                        return (
                          <StyledTableRow key={application.id ?? `${application.invoiceId ?? 'n-a'}-${index}`}>
                            <StyledTableCell>{dateAppliedText}</StyledTableCell>
                            <StyledTableCell>
                              {application.invoice?.invoiceNumber ?? application.invoiceId ?? 'N/A'}
                            </StyledTableCell>
                            <StyledTableCell >
                              {toDollarAmount(floatVal(application.amount ?? 0))}
                            </StyledTableCell>
                            <StyledTableCell >
                              {toDollarAmount(floatVal(application.taxAmount ?? 0))}
                            </StyledTableCell>
                            <StyledTableCell >{toDollarAmount(applicationTotal)}</StyledTableCell>
                            <StyledTableCell>{capitalize(application.hydraState ?? 'unknown')}</StyledTableCell>
                            <StyledTableCell>
                              <Button
                                size='small'
                                color='error'
                                variant='outlined'
                                startIcon={pendingUnapplyId === application.id
                                  ? <CircularProgress size={14} color='inherit' />
                                  : <DeleteIcon />}
                                onClick={() => handleUnapplyCreditApplication(application.id ?? null)}
                                disabled={
                                  application.hydraState !== 'active'
                                  || pendingUnapplyId !== null
                                  || isUnapplyingAll
                                }
                                title={
                                  application.hydraState !== 'active'
                                    ? 'Can only unapply active applications'
                                    : isUnapplyingAll
                                      ? 'Bulk unapply in progress'
                                      : 'Unapply this credit'
                                }
                              >
                                {pendingUnapplyId === application.id ? 'Unapplying…' : 'Unapply'}
                              </Button>
                            </StyledTableCell>
                          </StyledTableRow>
                        );
                      })
                    }
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )
        }

        <Grid container mt={2} spacing={1.5}>
          {
            allowEdits
            && (
              <Grid item xs={12} mb={2}>
                <Box display='flex' gap={1.5}>
                  <Button
                    disabled={(creditNote.credits ?? []).length === 0 || isCreatingCreditNote}
                    onClick={() => { handleCreateCreditNote(false); }}
                    variant='contained'
                    startIcon={isCreatingCreditNote ? <CircularProgress size={20} color='inherit' /> : undefined}
                  >
                    {isCreatingCreditNote ? 'Creating...' : 'Create'}
                  </Button>
                  <Button
                    disabled={(creditNote.credits ?? []).length === 0 || isCreatingCreditNote}
                    onClick={() => { handleCreateCreditNote(true); }}
                    variant='contained'
                    color='secondary'
                    startIcon={isCreatingCreditNote ? <CircularProgress size={20} color='inherit' /> : undefined}
                  >
                    {isCreatingCreditNote ? 'Creating & Publishing...' : 'Create & Publish'}
                  </Button>
                </Box>
              </Grid>
            )
          }

          {
            canBeDeleted
            && (
              <Grid item container xs={12}>
                <Grid item>
                  <Button
                    color='error'
                    variant='outlined'
                    onClick={
                      () => {
                        setConfirmationModalOnConfirm(
                          () => () => setCreditNote((old) => {
                            const newCreditNote = { ...old, state: EHydraCreditNoteState.deleted };

                            onSave(newCreditNote);
                            return newCreditNote;
                          }),
                        );

                        setConfirmationModalPromptText('Are you sure you want to delete this Credit Note?');

                        setShowConfirmationModal(true);
                      }
                    }
                  >
                    Delete Credit Note
                  </Button>
                </Grid>
              </Grid>
            )
          }
          <Grid item container xs={12} spacing={1.5} alignItems='flex-start'>
            <Grid item container pl={1.5} xs={12} lg={6}>
              <CreditNoteNotesSection
                incomingCreditNote={incomingCreditNote}
                onSave={onSave}
                allowEdits={allowEdits}
                setCreditNote={setCreditNote}
              />
            </Grid>

            <Grid item container xs={12} lg={6}>
              <CreditNoteVoidSection
                creditNote={creditNote}
                onSave={onSave}
                setConfirmationModalPromptText={setConfirmationModalPromptText}
                setShowConfirmationModal={setShowConfirmationModal}
                setConfirmationModalOnConfirm={setConfirmationModalOnConfirm}
                setCreditNote={setCreditNote}
              />
            </Grid>
          </Grid>

        </Grid>

        <ConfirmationModal
          open={showConfirmationModal}
          setOpen={setShowConfirmationModal}
          promptText={confirmationModalPromptText}
          onConfirm={confirmationModalOnConfirm}
          onCancel={() => setShowConfirmationModal(false)}
          onClose={() => setShowConfirmationModal(false)}
        />
      </Grid>

      <ConfirmationModal
        open={showApplyAllModal}
        setOpen={setShowApplyAllModal}
        dialogTitle='Confirm Applying All Credits to Source Invoices'
        promptText="Are you sure that you want to create a payment for each credit of this credit note?"
        onConfirm={async () => {
          try {
            if (creditNote.id) {
              await applyCreditBalanceToSourceInvoices(creditNote.id);
            }
          } finally {
            setShowApplyAllModal(false);
          }
        }}
      />

      <ApplyToInvoiceModal
        open={showApplyToInvoiceModal}
        onClose={() => setShowApplyToInvoiceModal(false)}
        selectedCreditNotes={[creditNote as unknown as TCreditNoteWithBillingContacts]}
      />
    </>
  );
};
